// src/i18n/en/activityMonitor.ts

const activityMonitor = {
  name: 'Activity Monitor',
  title: 'Live Activity Monitor',
  subtitle: 'Real-time device activity dashboard',
  
  filters: {
    hotel: 'Property',
    group: 'Group',
    deviceType: 'Device Type',
    status: 'Activity Status',
    allHotels: 'All Properties',
    allTypes: 'All Types',
    allStatuses: 'All Statuses',
    timeRange: 'Time Range',
    allGroups: 'All groups · last hour',
    ungrouped: 'Ungrouped',
  },
  
  actions: {
    toggleFilters: 'Toggle Filters',
    refresh: 'Refresh',
    viewJourney: 'View Journey',
    viewEvents: 'View Events',
    viewErrors: 'View Errors',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    groupMonitor: 'Group Monitor',
    onlyAttention: 'Attention only',
    showAll: 'Show all',
    viewDetail: 'View detail',
    viewSettings: 'View options',
  },

  // Read-only panel view options (settings icon)
  settings: {
    fitWidth: 'Fit to screen width',
  },

  // Attention lifecycle (acknowledge / keep)
  attention: {
    acknowledge: 'Acknowledge',
    keep: 'Keep',
    unmark: 'Remove',
    mark: 'Flag',
    pinned: 'Pinned',
    acknowledged: 'Handled',
    expired: 'Unattended',
  },

  // Operational health verdict (banner + counters)
  health: {
    allOperational: 'All operational',
    needsAttention: '%{count} unit needs attention |||| %{count} units need attention',
    allOnlineNoSessions: 'All units online · no active sessions',
    active: 'Active',
    idle: 'Idle',
    offline: 'Offline',
    errorsToday: 'Errors today',
    attentionTooltip: 'Units offline or with errors',
  },

  hasErrors: 'There are errors on this device',
  
  summary: {
    total: 'Total Devices',
    active: 'Active',
    idle: 'Idle',
    offline: 'Offline',
    totalDevices: 'Devices',
    totalSessions: 'Sessions',
    activeSessions: 'Active',
    completedSessions: 'Completed',
    flowingOk: 'all flowing normally',
    finishedOk: 'completed successfully',
  },

  status: {
    active: 'Active (last activity %{minutes} min ago)',
    idle: 'Idle (%{minutes} min inactive)',
    offline: 'Offline (%{minutes} min)',
    inactive: 'Device Inactive',
    deviceInactive: 'Device Deactivated',
  },
  
  statusOptions: {
    active: 'Active',
    idle: 'Idle',
    offline: 'Offline',
    inactive: 'Inactive',
  },
  
  time: {
    justNow: 'Just now',
    minutesAgo: '%{count} min ago',
    hoursAgo: '%{count} hour ago |||| %{count} hours ago',
    daysAgo: '%{count} day ago |||| %{count} days ago',
  },
  
  labels: {
    sessions: 'sessions',
    button: 'Labels',
    free: 'No category',
  },

  reservation: 'Reservation',
  section: 'Section',
  recentActivity: 'Recent Activity',
  lastEvent: 'Last Event',
  sessions: 'sessions',
  activeSessions: 'active sessions',
  completedSessions: 'completed sessions',
  noActiveSessions: 'No active sessions',
  eventTimeline: 'Event timeline',
  completedAt: 'Completed',
  
  sections: {
    s: 'Reservation lookup',
    r: 'Room',
    i: 'Identification',
    f: 'Signature',
    u: 'Extras',
    b: 'Bill summary',
    p: 'Payment',
    k: 'Key',
    checkin: 'Check-in',
    checkout: 'Check-out',
    keydispensing: 'Key Dispensing',
    invoicegeneration: 'Invoice Generation',
    guestregistration: 'Guest Registration',
    // Redesigned operations panel — section headers
    attention: 'Needs attention now',
    inProgress: 'Sessions in progress',
    finished: 'Recently finished',
    stepIdentify: 'Identifying',
    stepPay: 'Paying',
    stepKey: 'Collecting key',
    completedRecent: 'Completed · recent',
    emptyStep: 'No one at this step right now',
    emptyDevice: 'No activity',
    idleGroup: 'No activity',
    idleTitle: 'No activity',
    idleSubtitle: 'All in order. The points are ready for the next guest.',
  },
  
  metrics: {
    eventsMin: 'Events/min',
    avgTime: 'Avg Time',
  },
  
  messages: {
    noRecentActivity: 'No activity in the last 24 hours',
    noDevices: 'No devices found',
    deviceOffline: 'Device is offline',
    noActivity: 'No recent activity',
    selectDifferentFilters: 'Try selecting different filters to see devices',
    liveMonitoring: 'Live Monitoring',
    moreEvents: 'More events',
  },
  
  sessionStatus: {
    active: 'Active',
    completed: 'Completed',
    abandoned: 'Incomplete',
  },
  
  completed: 'Completed',
  
  eventCodes: {
    HB: 'Heartbeat',
    
    // Check-in flow
    CI_START: 'Check-In Started',
    CI_SEARCH: 'Search Reservation',
    CI_IDENTIFY: 'Identification',
    CI_SIGN: 'Signature',
    CI_UPSELL: 'Upselling',
    CI_COMP: 'Check-In Completed',
    
    // Pre-check-in flow
    PC_LINK_OPEN: 'Link Opened',
    PC_START: 'Pre-Check-In Started',
    PC_SEARCH: 'Search Reservation',
    PC_IDENTIFY: 'Identification',
    PC_SIGN: 'Signature',
    PC_UPSELL: 'Upselling',
    PC_COMP: 'Pre-Check-In Completed',
    
    // Payment
    PM_SUCCESS: 'Payment Success',
    PM_ERROR: 'Payment Error',
    
    // Key delivery
    KD_SUCCESS: 'Key Dispensed',
    KD_ERROR: 'Key Error',
    KD_REBOOT: 'Dispenser Reboot',

    // Section navigation (kiosk)
    SECTION_ENTER: 'Section Entered',
    SECTION_EXIT: 'Section Exited',

    // Operational / errors
    PMS_RECOVERY_FAILED: 'Recovery Failed',
    PMS_RESERVATION_NOT_FOUND: 'Reservation Not Found',
    PMS_ROOM_NOT_READY: 'Room Not Ready',
    PMS_EARLY_ARRIVAL: 'Early Arrival',
    PMS_CHECKIN_FAILED: 'Check-In Failed',
    PMS_TIMEOUT: 'Reservation system timeout',
    ERR: 'Error',
    AB: 'Abandoned',

    // Kiosk 1.1.9 error codes (telemetry contract §4)
    PMS_ADD_GUEST_FAILED: 'Guest Registration Failed',
    PMS_SEND_DOCS_FAILED: 'Document Delivery Failed',
    TRAVELER_FORM_GENERATION_FAILED: 'Traveler Form Generation Failed',
    STAY_SAVE_FAILED: 'Stay Registration Failed',
    CHECKIN_PIPELINE_FAILED: 'Check-In Process Error',
    PMS_BOOKING_FORM_NOT_FOUND: 'Pre-Registration Not Found',
    PMS_TOURIST_TAX_FAILED: 'Tourist Tax Calculation Failed',
    UI_IDLE_RESET: 'Inactivity Reset',
    UI_SESSION_EXPIRED: 'Session Expired',
    SCAN_RETRIES_EXHAUSTED: 'Scan Retries Exhausted',
    PAYMENT_FAILED: 'Payment Registration Failed',
    PAYMENT_CONFIRM_FAILED: 'Payment Confirmation Failed',
    PAYMENT_TERMINAL_ERROR: 'Payment Terminal Error',
    PAYMENT_STATUS_NULL: 'Payment Aborted',
    PAYMENT_POLLING_TIMEOUT: 'Payment Status Timeout',
    PMS_REVERSE_CHECKIN_FAILED: 'Check-In Reversal Failed',
    CASH_JAM: 'Cash Machine Jam',
    CASH_FRAUD: 'Cash Fraud Attempt Detected',
    CASH_DISPENSE_ERROR: 'Cash Dispense Error',
    CASH_INIT_ERROR: 'Cash Machine Initialization Error',
    CASH_AMOUNT_ERROR: 'Cash Amount Error',
    CASH_COMM: 'Cash Machine Communication Error',
    CASH_NO_CHANGE: 'No Change Available',
    CASH_INSUFFICIENT_FUNDS: 'Insufficient Cash Funds',
    PMS_CHECKOUT_FAILED: 'Check-Out Rejected',
    PMS_CHECKOUT_OUTSTANDING_BALANCE: 'Outstanding Balance',
    PMS_CHECKOUT_EARLY_BLOCKED: 'Early Check-Out Blocked',
    PMS_INVOICE_INVALID_INFO: 'Invalid Billing Information',
    PMS_INVOICE_NO_SERIES: 'Invoice Series Not Configured',
    PMS_INVOICE_ROOM_INVALID: 'Invoice Room Invalid',
    PMS_INVOICE_CHARGES_UNAVAILABLE: 'Charges Not Available',
    PMS_INVOICE_NOT_FOUND: 'Invoice Not Found',
    PMS_INVOICE_SECOND_SURNAME_REQUIRED: 'Second Surname Required',
    UPSELL_ORDER_FAILED: 'Additional Service Order Failed',
    UPSELL_EXTERNAL_FAILED: 'External Service Confirmation Failed',
    UPSELL_NOT_AVAILABLE: 'Additional Service Not Available',
    LOCKS_COMM: 'Lock System Communication Error',
    LOCKS_AUTH_ERROR: 'Lock System Authentication Error',
    LOCKS_READ_ERROR: 'Lock System Read Error',
    KD_UID_NOT_REGISTERED: 'Card Registration Rejected',
    KD_DIGITAL_KEY_DELIVERY_FAILED: 'Digital Key Delivery Failed',
    KEY_DISPENSE_FAILED: 'Key Dispense Failed',
    SCANNER_COMM: 'Document Scanner Communication Error',
    QR_READER_COMM: 'QR Reader Communication Error',
    SIGNATURE_COMM: 'Signature Service Communication Error',
    PRINTER_FAILED: 'Printer Failure',
    PRINTER_PAPER_OUT: 'Printer Out of Paper',
    PRINTER_PAPER_LOW: 'Printer Paper Low',
    NETWORK_UNREACHABLE: 'Kiosk Out of Service (Network)',
    NETWORK_RECOVERED: 'Connection Recovered',
    KIOSK_NOT_REGISTERED: 'Kiosk Not Registered',
    KIOSK_INACTIVE: 'Kiosk Inactive',
    KIOSK_BLOCKED_TEMPORARY: 'Kiosk Temporarily Blocked',
    KIOSK_BLOCK_RECOVERED: 'Kiosk Block Lifted',
  },
  
  channels: {
    online: 'Online pre-check-in',
    airLobby: 'Air Lobby',
    kiosk: 'Kiosk',
    mobileKiosk: 'Mobile kiosk',
    dispenser: 'Key dispenser',
  },

  // Global KPIs
  kpi: {
    checkIns: 'Check-ins',
    keys: 'Keys',
    dispenserOps: 'Dispenser',
  },

  // Daily statistics
  stats: {
    today: 'Today',
    sessions: 'Sessions',
    successful: 'Successful',
    failed: 'Failed',
    errors: 'Errors',
    avgDuration: 'Avg. duration',
    keysDelivered: 'Keys',
  },

  // Session details
  session: {
    room: 'Room',
    duration: 'Duration',
    keys: 'Keys',
    error: 'Error',
    waiting: 'waiting',
    inProgress: 'in progress',
    finished: 'finished',
  },

  // Dispenser card
  dispenserCard: {
    keysToday: 'keys today',
    errorsToday: 'errors today',
    operations: 'operations',
    noOperations: 'No operations yet',
    keyDispensed: 'Key dispensed',
    error: 'Error',
    abandoned: 'Incomplete',
    room: 'Room',
    heartbeatInfo: 'Dispensers report every 30 min. It may take up to 35 min to detect a disconnection.',
  },

  // Time range filter
  timeRange: {
    '1h': '1h',
    '6h': '6h',
    '12h': '12h',
    '24h': '24h',
    '48h': '48h',
    '72h': '3d',
    '7d': '7d',
  },

  sessionState: {
    error: 'Problem',
    notice: 'Notice',
    mistake: 'Incomplete',
    slow: 'Slow',
    flowing: 'In progress',
    completed: 'Completed',
    abandoned: 'Incomplete',
  },

  step: {
    search: 'Reservation lookup',
    identify: 'Identification',
    pay: 'Payment',
    key: 'Key',
  },

  point: {
    checkin: 'Check-in point',
    dispenser: 'Key delivery',
    dispenserDevice: 'Key dispenser',
    unidentified: 'unidentified guest',
  },

  view: {
    atencion: 'Attention',
    paso: 'Step',
    equipo: 'Device',
  },

  density: {
    comfortable: 'Comfortable',
    compact: 'Compact',
  },

  layout: {
    grid: 'Grid',
    columns: 'Columns',
  },

  // Device connection state (from the backend activityStatus)
  connection: {
    active: 'Connected',
    idle: 'Idle',
    offline: 'Disconnected',
  },

  // Human names for the journey sections (from the event metadata 'section',
  // which arrives either as a word like "home" or a single-letter code S/I/F/U/P/K)
  sectionNames: {
    s: 'Home',
    i: 'Identification',
    f: 'Form',
    u: 'Add-ons',
    p: 'Payment',
    k: 'Key delivery',
    r: 'Room Selection',
    b: 'Bill summary',
    home: 'Home',
    identification: 'Identification',
    identify: 'Identification',
    search: 'Search',
    signature: 'Signature',
    sign: 'Signature',
    payment: 'Payment',
    pay: 'Payment',
    upsell: 'Add-ons',
    key: 'Key delivery',
    keydelivery: 'Key delivery',
    keydispensing: 'Key delivery',
    summary: 'Summary',
    complete: 'Completion',
    completion: 'Completion',
  },

  // At-a-glance session detail (modal summary)
  detail: {
    startedAt: 'Started',
    elapsed: 'In progress',
    duration: 'Duration',
    reservation: 'Reservation',
    unit: 'Unit',
    keysDelivered: 'Keys delivered',
    channel: 'Channel',
    recentEvents: 'Recent events',
    openJourney: 'Open journey',
    openEvents: 'Open events',
    openLogs: 'Open device logs',
    close: 'Close',
    live: 'Live',
    // Enriched event detail
    eventDetail: 'Event detail',
    enteredSection: 'Entered',
    exitedSection: 'Exited',
    section: 'Section',
    room: 'Room',
    whatHappened: 'What happened',
    recommendation: 'Recommendation',
    technicalInfo: 'Technical info',
    errorCode: 'Code',
    metadata: 'Additional data',
    noExtraInfo: 'No additional information for this event.',
    loadingEvents: 'Loading events…',
    tapForDetail: 'Tap an event to see the full detail',
    copyData: 'Copy data',
    copied: 'Data copied',
    notification: {
      title: 'Who was notified?',
      sentTo: 'Notified',
      notSent: 'No notification sent',
      failed: 'Notification delivery failed',
      channels: {
        email: 'email',
        sms: 'SMS',
        whatsapp: 'WhatsApp',
        other: 'notification',
      },
      reasons: {
        ThrottleActive: 'repeated notification (temporarily muted)',
        NoRecipientsConfigured: 'no recipients configured',
        ConfigDisabledOrMissing: 'notifications not configured',
        NotifierNotConfigured: 'delivery not configured',
        NoEnabledChannels: 'no active channels',
        ThresholdNotReached: 'threshold not reached',
        DeliveryError: 'delivery error',
      },
    },
    severity: {
      Critical: 'Critical',
      High: 'High',
      Medium: 'Medium',
      Low: 'Low',
      Info: 'Info',
      Warning: 'Warning',
      Error: 'Error',
      Informational: 'Info',
    },
  },

  // Device notices — equipment telemetry with no guest session.
  deviceNotice: {
    zoneTitle: 'Device notices',
    attention: 'Need attention',
    informational: 'Informational',
    times: 'seen %{count} time |||| seen %{count} times',
    lastSeen: 'Last seen',
    occurrences: 'Occurrences',
    kind: {
      critical: 'Critical notice',
      maintenance: 'Maintenance notice',
      info: 'Information',
    },
    titles: {
      cardLow: 'Card hopper low',
      cardEmpty: 'Card hopper empty',
      reboot: 'Device restarted',
      doorOpen: 'Service door open',
      maxCards: 'Maximum cards reached',
      cardFront: 'Card in the output slot',
      encoder: 'Card encoder not detected',
      comm: 'No communication with an external system',
      network: 'Device connectivity issue',
      recovery: 'Recovery retry',
      systemError: 'Internal device error',
      jam: 'Device jam',
      generic: 'Device notice',
    },
    descs: {
      cardLow: 'The hopper should be refilled soon. It does not block key delivery.',
      cardEmpty: 'No cards left. Upcoming deliveries will fail until the hopper is refilled.',
      reboot: 'The device is operational again. No action is required.',
      doorOpen: 'Close the service door to continue operation.',
      maxCards: 'The maximum number of cards for the reservation has been reached.',
      cardFront: 'Remove the card inserted in the output slot.',
      encoder: 'The card encoder is not detected. Check the device.',
      comm: 'The device cannot reach an external system. Check it when possible.',
      network: 'The device had connectivity issues. Check the connection.',
      recovery: 'The device retried an automatic recovery.',
      systemError: 'The device logged an internal error. Review it when possible.',
      jam: 'Possible jam. Check the device mechanism.',
      generic: 'The device raised a notice. Review it when possible.',
    },
  },

  issues: {
    documentInvalid: 'Invalid document',
    qrInvalid: 'Invalid QR code',
    reservationNotFound: 'Reservation not found',
    roomNotReady: 'Room not ready',
    earlyArrival: 'Early arrival',
    checkinFailed: 'Check-in could not be completed',
    paymentFailed: 'Payment declined',
    keyFailed: 'Could not deliver the key',
    deviceFault: 'Check the device',
    generic: 'Issue detected',
    notice: 'Device notice',
    cardLow: 'Running low on cards',
    maxCards: 'Maximum cards for the reservation reached',
    slowIdentify: 'Taking a while to identify',
    slowPay: 'Taking a while to pay',
    slowKey: 'Taking a while to collect the key',
    slowGeneric: 'Taking a while in the process',
  },

  updatedAgo: 'updated %{time} ago',
};

export default activityMonitor;
